import { createStore } from "zustand";
import { defaultCellLayoutTemplate } from "../constants/cellLayoutTemplates";
import { GUTTER_SIZE as DEFAULT_GUTTER_SIZE } from "../constants/theme";
import { DEFAULT_COMIC_STYLE_KEY } from "../constants/comicStyles";
import { getDarkModeSetting, setDarkModeSetting } from "../hooks/useDarkModeSetting";
import { trackEvent } from "../utils/analytics";

function cloneCells(cells) {
  return cells.map((cell) => ({
    ...cell,
    items: (cell.items || []).map((item) => ({ ...item })),
  }));
}

// Layout config travels with each undo step so geometry and settings never
// disagree after undoing a gutter, page-size, or template change.
const LAYOUT_CONFIG_KEYS = ["gutterSize", "pageSizeKey", "layoutTemplateKey", "storyboard"];

function snapshotOf(state) {
  const entry = {
    cells: cloneCells(state.cells),
    selection: state.selection ? { ...state.selection } : null,
  };
  LAYOUT_CONFIG_KEYS.forEach((key) => { entry[key] = state[key]; });
  return entry;
}

function restoreFrom(entry) {
  const restored = {
    cells: cloneCells(entry.cells),
    selection: entry.selection ? { ...entry.selection } : null,
  };
  LAYOUT_CONFIG_KEYS.forEach((key) => {
    if (entry[key] !== undefined) restored[key] = entry[key];
  });
  return restored;
}

export const MIN_ZOOM = 0.2;
export const MAX_ZOOM = 4;

export const createEditorStore = () =>
  createStore((set, get) => ({
    // ─── Core data ───────────────────────────────────────────────────
    cells: [],
    selection: null, // null | { type: "cell"|"bubble"|"sfx", cellId, bubbleId?, sfxId? }

    // ─── Undo / redo ─────────────────────────────────────────────────
    history: [],
    future: [],
    // Open drag/slider gesture: { startCells, entry } — collapses every
    // intermediate update into a single undo step committed on endGesture.
    gesture: null,

    // ─── Layout config ───────────────────────────────────────────────
    layoutTemplateKey: defaultCellLayoutTemplate.key,
    gutterSize: DEFAULT_GUTTER_SIZE,
    pageSizeKey: "letter",
    // Board settings when this page is a storyboard, else null (a comic page)
    storyboard: null,

    // ─── First-run sample page (shows the "clear it" card) ───────────
    isSample: false,

    // ─── Comic style preset (page skin) ──────────────────────────────
    comicStyleKey: DEFAULT_COMIC_STYLE_KEY,

    // ─── Export config ───────────────────────────────────────────────
    exportDpi: 150,
    exportFormat: "pdf",

    // ─── Snap config ─────────────────────────────────────────────────
    snapGridSize: 10,
    snapEnabled: true,
    showSnapGrid: false,

    // ─── View config ─────────────────────────────────────────────────
    // Canvas zoom. "fit" tracks the workspace size; any manual zoom sticks.
    zoom: 1,
    zoomMode: "fit",
    // Knife tool: while on, dragging across a panel cuts it (⌥-drag always does)
    knifeMode: false,
    showGuides: false,
    showReadingOrder: false,
    readingDirection: "ltr",
    darkMode: getDarkModeSetting(),

    // ─── UI state ────────────────────────────────────────────────────
    showAIModal: false,
    // A short message pinned to the desk (refused actions, save problems)
    notice: null,
    contextMenu: null,
    clipboardBubble: null,
    multiSelect: [],
    containerSize: { width: 0, height: 0 },
    isInitialized: false,

    // ─── Actions: initialization ──────────────────────────────────────
    setInitialized: (cells, partialState = {}) =>
      set({ cells, isInitialized: true, history: [], future: [], gesture: null, selection: null, ...partialState }),

    setContainerSize: (size) => set({ containerSize: size }),

    // ─── Actions: cells ──────────────────────────────────────────────
    setCells: (cells) => set({ cells }),

    /**
     * Apply an updater function to cells, automatically pushing the
     * current state onto the history stack.
     */
    applyCellUpdate: (updaterFn) => {
      const { cells, history, gesture } = get();
      const nextCells = updaterFn(cells);
      if (nextCells === cells) return;

      // Inside a gesture, the snapshot taken at beginGesture covers this change
      if (gesture) {
        set({ cells: nextCells });
        return;
      }

      const nextHistory = [...history, snapshotOf(get())];
      if (nextHistory.length > 50) nextHistory.shift();

      set({ cells: nextCells, history: nextHistory, future: [] });
    },

    /**
     * Snapshot current state onto history (for multi-step operations
     * that call setCells directly afterwards). No-op inside a gesture.
     */
    captureSnapshot: () => {
      const { history, gesture } = get();
      if (gesture) return;
      const nextHistory = [...history, snapshotOf(get())];
      if (nextHistory.length > 50) nextHistory.shift();
      set({ history: nextHistory, future: [] });
    },

    // ─── Actions: gestures ───────────────────────────────────────────
    /**
     * Start a continuous interaction (drag, resize, slider scrub). All
     * cell changes until endGesture become one undo step.
     */
    beginGesture: () => {
      const state = get();
      if (state.gesture) return;
      set({
        gesture: {
          startCells: state.cells,
          startGutter: state.gutterSize,
          entry: snapshotOf(state),
        },
      });
    },

    /** Finish the gesture; records an undo step only if something changed. */
    endGesture: () => {
      const { gesture, cells, gutterSize, history } = get();
      if (!gesture) return;
      if (cells === gesture.startCells && gutterSize === gesture.startGutter) {
        set({ gesture: null });
        return;
      }
      const nextHistory = [...history, gesture.entry];
      if (nextHistory.length > 50) nextHistory.shift();
      set({ gesture: null, history: nextHistory, future: [] });
    },

    // ─── Actions: undo / redo ─────────────────────────────────────────
    undo: () => {
      get().endGesture();
      const { history, future } = get();
      if (history.length === 0) return;
      trackEvent("undo");
      const last = history[history.length - 1];
      const nextFuture = [...future, snapshotOf(get())];
      if (nextFuture.length > 50) nextFuture.shift();
      set({
        ...restoreFrom(last),
        history: history.slice(0, -1),
        future: nextFuture,
      });
    },

    redo: () => {
      get().endGesture();
      const { future, history } = get();
      if (future.length === 0) return;
      trackEvent("redo");
      const next = future[future.length - 1];
      const nextHistory = [...history, snapshotOf(get())];
      if (nextHistory.length > 50) nextHistory.shift();
      set({
        ...restoreFrom(next),
        future: future.slice(0, -1),
        history: nextHistory,
      });
    },

    // ─── Actions: layer ordering ────────────────────────────────────────
    reorderItem: (cellId, itemId, direction) => {
      get().applyCellUpdate((cells) => {
        return cells.map((cell) => {
          if (cell.id !== cellId) return cell;
          const items = [...(cell.items || [])];
          const idx = items.findIndex((i) => i.id === itemId);
          if (idx < 0) return cell;
          // Don't reorder backgrounds or tipCards
          const item = items[idx];
          if (item.type === "background" || item.type === "tipCard") return cell;
          // Content items can't move below backgrounds
          const firstContentIdx = items.findIndex((i) => i.type !== "background");
          let newIdx = idx;
          switch (direction) {
            case "forward": newIdx = Math.min(items.length - 1, idx + 1); break;
            case "backward": newIdx = Math.max(firstContentIdx, idx - 1); break;
            case "toFront": newIdx = items.length - 1; break;
            case "toBack": newIdx = firstContentIdx; break;
            default: break;
          }
          if (newIdx === idx) return cell;
          items.splice(idx, 1);
          items.splice(newIdx, 0, item);
          return { ...cell, items };
        });
      });
      trackEvent("layer_reordered", { direction });
    },

    // ─── Actions: selection ───────────────────────────────────────────
    setSelection: (selection) => set({ selection }),
    clearSelection: () => set({ selection: null }),
    setMultiSelect: (ids) => set({ multiSelect: ids }),

    // ─── Actions: layout config ───────────────────────────────────────
    setLayoutTemplateKey: (key) => set({ layoutTemplateKey: key }),
    setGutterSize: (size) => set({ gutterSize: size }),
    setPageSizeKey: (key) => set({ pageSizeKey: key }),
    setStoryboard: (storyboard) => set({ storyboard }),
    setIsSample: (v) => set({ isSample: v }),
    setComicStyleKey: (key) => {
      set({ comicStyleKey: key });
      trackEvent("comic_style_selected", { style_key: key });
    },

    // ─── Actions: export config ───────────────────────────────────────
    setExportDpi: (dpi) => set({ exportDpi: dpi }),
    setExportFormat: (fmt) => set({ exportFormat: fmt }),

    // ─── Actions: snap config ──────────────────────────────────────────
    setSnapGridSize: (size) => set({ snapGridSize: size }),
    toggleSnapEnabled: () => set((s) => ({ snapEnabled: !s.snapEnabled })),
    toggleShowSnapGrid: () => set((s) => ({ showSnapGrid: !s.showSnapGrid })),

    // ─── Actions: view config ─────────────────────────────────────────
    setZoom: (zoom, mode = "manual") => {
      // Ignore impossible values (e.g. measuring a not-yet-laid-out desk)
      if (!Number.isFinite(zoom) || zoom <= 0) return;
      set({ zoom: Math.min(MAX_ZOOM, Math.max(MIN_ZOOM, zoom)), zoomMode: mode });
    },
    setKnifeMode: (on) => set({ knifeMode: on }),
    toggleGuides: () => set((s) => ({ showGuides: !s.showGuides })),
    toggleReadingOrder: () => set((s) => ({ showReadingOrder: !s.showReadingOrder })),
    setReadingDirection: (dir) => set({ readingDirection: dir }),
    toggleDarkMode: () => {
      const next = !get().darkMode;
      setDarkModeSetting(next);
      set({ darkMode: next });
    },

    // ─── Actions: UI state ────────────────────────────────────────────
    setShowAIModal: (v) => set({ showAIModal: v }),
    showNotice: (text, { sticky = false } = {}) => set({ notice: { text, sticky, at: Date.now() } }),
    clearNotice: () => set({ notice: null }),
    setContextMenu: (menu) => set({ contextMenu: menu }),
    setClipboardBubble: (b) => set({ clipboardBubble: b }),
  }));
